import React from 'react';
import { useModal } from '../../../../../../components/Modal/PresentedModal';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { t } from '../../../../../../locales/translate';
import { graphql } from '../../../../../../generated';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { FormSwitcher } from '../../../../../../components/Form/form_switcher/form_switcher';
import { Stack } from '../../../../../../components/Stack/Stack';
import { FormTextField } from '../../../../../../components/Form/TextField/FormTextField';
import { FormSelect } from '../../../../../../components/Form/Select/FormSelect';
import { handleError } from '../../../../../../model/notification';
import { FormForm } from '../../../../../../components/Form/FormForm';

interface Props {
  eventId: string;
}

const PUBLIC_TRANSPORT_QUERY = graphql(`
  query PublicTransportQuery($id: ID!) {
    event(id: $id) {
      id
      publicTransportConfig {
        id
        enabled
        description
        publicTransportCompany {
          id
        }
      }
    }
    publicTransportCompanies(first: 500000) {
      nodes {
        id
        name
      }
    }
  }
`);

const UPDATE_PUBLIC_TRANSPORT_CONFIG = graphql(`
  mutation UpdatePublicTransportConfig(
    $id: ID!
    $enabled: Boolean
    $description: String
    $publicTransportCompanyId: ID
  ) {
    updatePublicTransportConfig(
      input: {
        id: $id
        enabled: $enabled
        description: $description
        publicTransportCompanyId: $publicTransportCompanyId
      }
    ) {
      publicTransportConfig {
        id
        description
        enabled
        publicTransportCompany {
          id
          name
        }
      }
    }
  }
`);

export function EditPublicTransport({ eventId }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const { data } = useSuspenseQuery(PUBLIC_TRANSPORT_QUERY, {
    variables: { id: eventId },
  });
  const [update] = useMutation(UPDATE_PUBLIC_TRANSPORT_CONFIG);
  const { fields, onSubmit, valid, dirty } = useForm<{
    enabled?: boolean;
    publicTransportCompanyId?: string | null;
    description?: string | null;
  }>({
    model: {
      enabled: data.event.publicTransportConfig.enabled,
      publicTransportCompanyId: data.event.publicTransportConfig.publicTransportCompany?.id,
      description: data.event.publicTransportConfig.description,
    },
    validations: {
      publicTransportCompanyId: 'required',
    },
    onSubmit: async ({ changes }) => {
      await update({ variables: { ...changes, id: eventId } });
      close();
    },
    onSubmitError: (error) => handleError(error),
  });

  return (
    <Modal open={open} title={t.page.event.design.publicTransport.edit.title}>
      <FormForm canSubmit={valid && dirty} onSubmit={onSubmit} onClose={close}>
        <Stack>
          <FormSwitcher
            {...fields.enabled}
            labels={[t.general.yes, t.general.no]}
            label={t.page.event.design.publicTransport.edit.enabled}
          />
          {fields.enabled.value && (
            <Stack>
              <FormSelect
                {...fields.publicTransportCompanyId}
                label={t.page.event.design.publicTransport.edit.company}
                options={data.publicTransportCompanies.nodes.map((company) => ({
                  value: company.id,
                  label: company.name,
                }))}
                allowEmpty
                nullIfEmpty
              />
              <FormTextField
                {...fields.description}
                label={t.page.event.design.publicTransport.edit.description}
                name="description"
              />
            </Stack>
          )}
        </Stack>
      </FormForm>
    </Modal>
  );
}
